import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cityService } from './index';

// Query Keys
export const cityQueryKeys = {
  all: ['cities'],
  lists: () => [...cityQueryKeys.all, 'list'],
  list: (filters) => [...cityQueryKeys.lists(), { filters }],
  details: () => [...cityQueryKeys.all, 'detail'],
  detail: (id) => [...cityQueryKeys.details(), id],
};

// Queries
function normalizeCitiesPayload(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && Array.isArray(raw.cities)) return raw.cities;
  return [];
}

export const useCities = (filters = {}) => {
  return useQuery({
    queryKey: cityQueryKeys.list(filters),
    queryFn: () => cityService.getAllCities(),
    select: normalizeCitiesPayload,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useCity = (cityId) => {
  return useQuery({
    queryKey: cityQueryKeys.detail(cityId),
    queryFn: () => cityService.getCityById(cityId),
    enabled: !!cityId,
    staleTime: 10 * 60 * 1000,
  });
};

// Mutations
export const useCreateCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cityData) => cityService.createCity(cityData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cityQueryKeys.lists() });
    },
    onError: (error) => {
      console.error('City creation failed:', error);
    },
  });
};

export const useUpdateCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cityId, cityData }) => cityService.updateCity(cityId, cityData),
    onSuccess: (data, variables) => {
      const { cityId } = variables;

      // Update the specific city in cache
      queryClient.setQueryData(cityQueryKeys.detail(cityId), data);

      // Invalidate cities list
      queryClient.invalidateQueries({ queryKey: cityQueryKeys.lists() });
    },
    onError: (error) => {
      console.error('City update failed:', error);
    },
  });
};

export const useDeleteCity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cityId) => cityService.deleteCity(cityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cityQueryKeys.lists() });
    },
    onError: (error) => {
      console.error('City deletion failed:', error);
    },
  });
};

// Combined hook for city management operations
export const useCityManagement = () => {
  const citiesQuery = useCities();
  const createMutation = useCreateCity();
  const updateMutation = useUpdateCity();
  const deleteMutation = useDeleteCity();

  return {
    // Queries
    cities: citiesQuery.data || [],
    isLoading: citiesQuery.isLoading,
    error: citiesQuery.error,

    // Mutations
    createCity: createMutation.mutateAsync,
    updateCity: updateMutation.mutateAsync,
    deleteCity: deleteMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Refetch function
    refetch: citiesQuery.refetch,
  };
};
